import { router } from "@inertiajs/react"
import MediaGallery from "@/components/storefront/MediaGallery"
import VariantSelector from "@/components/storefront/VariantSelector"
import CollapsibleSection from "@/components/storefront/CollapsibleSection"
import Breadcrumb from "@/components/storefront/Breadcrumb"
import type { BreadcrumbCrumb } from "@/components/storefront/Breadcrumb"
import type {
  ItemDetailData,
  ItemLabels,
  MediaItem,
  Variant,
} from "@/components/storefront/types"

interface ItemDetailProps {
  item: ItemDetailData
  medias: MediaItem[]
  variants: Variant[]
  virtualStock: number
  store: { holiday: boolean; display_stock: boolean }
  addToCartUrl: string
  back: { url: string; label: string }
  labels: ItemLabels
  breadcrumb: BreadcrumbCrumb[]
}

export default function ItemDetail({
  item,
  medias,
  variants,
  virtualStock,
  store,
  addToCartUrl,
  back,
  labels,
  breadcrumb,
}: ItemDetailProps) {
  const isPreSale = item.pre_sale || item.internal_pre_sale
  const hasMultipleVariants = variants.length > 1
  const defaultVariant = variants[0]

  const handleSingleAddToCart = (e: React.FormEvent) => {
    e.preventDefault()
    if (!defaultVariant) return
    router.post(
      addToCartUrl,
      { variant_id: defaultVariant.id },
      { preserveScroll: true }
    )
  }

  return (
    <>
      <div className="bg-white min-h-screen pt-36">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-8">
          <a
            href={back.url}
            className="inline-flex items-center border border-contrast rounded-md px-4 py-2 text-sm font-medium text-contrast hover:bg-contrast hover:text-light transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" />
            {back.label}
          </a>
        </div>
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
          <Breadcrumb items={breadcrumb} className="mb-6" />
          <div className="lg:flex lg:gap-x-8 lg:items-start">
            <div className="lg:w-7/12">
              <MediaGallery medias={medias} zoomScale={2.5} />
            </div>

            <div className="mt-10 px-4 sm:mt-16 sm:px-0 lg:mt-0 lg:w-5/12 lg:pl-28 lg:self-stretch">
              <div className="lg:sticky lg:top-36">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {item.name}
                </h1>

                {isPreSale && (
                  <div className="mt-3">
                    <span
                      title={labels.pre_sale_tooltip ?? undefined}
                      className="inline-block bg-gray-900 text-white text-sm font-medium px-3 py-1 rounded whitespace-nowrap cursor-help"
                    >
                      {labels.pre_sale}
                    </span>
                  </div>
                )}

                <div className="mt-3">
                  <h2 className="sr-only">{labels.product_information}</h2>
                  <p className="text-2xl tracking-tight text-gray-900 font-bold">
                    {item.price}
                  </p>
                </div>

                {hasMultipleVariants ? (
                  <VariantSelector
                    variants={variants}
                    addToCartUrl={addToCartUrl}
                    labels={{
                      size: labels.size,
                      add_to_cart: labels.add_to_cart,
                      in_stock_template: labels.in_stock_template,
                      last_one: labels.last_one,
                      out_of_stock: labels.out_of_stock,
                    }}
                  />
                ) : (
                  <SingleVariantPurchase
                    item={item}
                    virtualStock={virtualStock}
                    holiday={store.holiday}
                    displayStock={store.display_stock}
                    labels={labels}
                    onSubmit={handleSingleAddToCart}
                  />
                )}

                <section
                  className="mt-12"
                  aria-labelledby="details-heading"
                >
                  <h2 id="details-heading" className="sr-only">
                    {labels.product_information}
                  </h2>
                  <div className="divide-y divide-gray-200 border-t border-gray-200">
                    {item.product_fit !== null && (
                      <CollapsibleSection title={labels.product_fit}>
                        <ProductFitSlider
                          fit={item.product_fit}
                          smallLabel={labels.fit_small}
                          trueLabel={labels.fit_true_to_size}
                          largeLabel={labels.fit_large}
                        />
                      </CollapsibleSection>
                    )}

                    {item.description && (
                      <CollapsibleSection title={labels.product_story}>
                        <div className="prose prose-sm text-gray-600">
                          <p>{item.description}</p>
                        </div>
                      </CollapsibleSection>
                    )}

                    <CollapsibleSection title={labels.shipping_information}>
                      <div className="prose prose-sm text-gray-600">
                        <p>{item.shipping_information || labels.shipping_text}</p>
                      </div>
                    </CollapsibleSection>

                    {(item.details || item.specifications.length > 0) && (
                      <CollapsibleSection title={labels.details}>
                        <div className="prose prose-sm text-gray-600">
                          {item.details && <p>{item.details}</p>}
                          {item.specifications.length > 0 && (
                            <ul className="list-disc leading-8 pl-3 mt-2">
                              {item.specifications.map((spec) => (
                                <li key={spec}>{spec}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </CollapsibleSection>
                    )}

                    {item.sizing_info_url && (
                      <CollapsibleSection title={labels.sizing_infos}>
                        <img
                          src={item.sizing_info_url}
                          alt={labels.sizing_infos}
                          className="w-full max-w-md mx-auto"
                        />
                      </CollapsibleSection>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function ProductFitSlider({
  fit,
  smallLabel,
  trueLabel,
  largeLabel,
}: {
  fit: number
  smallLabel: string
  trueLabel: string
  largeLabel: string
}) {
  const percentage = (fit - 1) * 25
  return (
    <>
      <div className="relative mb-2">
        <div className="h-1 bg-gray-200 rounded-full" />
        <div
          className="absolute top-0 h-1 bg-gray-900 rounded-full"
          style={{ width: `${percentage}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-900 rounded-full"
          style={{ left: `calc(${percentage}% - 6px)` }}
        />
      </div>
      <div className="flex justify-between text-sm text-gray-600 mt-3">
        <span>{smallLabel}</span>
        <span>{trueLabel}</span>
        <span>{largeLabel}</span>
      </div>
    </>
  )
}

function SingleVariantPurchase({
  item,
  virtualStock,
  holiday,
  displayStock,
  labels,
  onSubmit,
}: {
  item: ItemDetailData
  virtualStock: number
  holiday: boolean
  displayStock: boolean
  labels: ItemLabels
  onSubmit: (e: React.FormEvent) => void
}) {
  if (item.pre_sale && item.pre_sale_url) {
    return (
      <div className="mt-6">
        <a
          href={item.pre_sale_url}
          target="_blank"
          rel="noreferrer"
          className="btn btn-primary mb-4 w-full"
        >
          {labels.participate_crowdfunding}
        </a>
      </div>
    )
  }

  if (holiday) return <div className="mt-6" />

  if (virtualStock > 0) {
    return (
      <form onSubmit={onSubmit} className="mt-6">
        <button type="submit" className="btn btn-primary w-full">
          {labels.add_to_cart}
        </button>
        {displayStock && (
          <p className="text-sm text-gray-500 mt-2">{labels.in_stock}</p>
        )}
        {!displayStock && virtualStock === 1 && (
          <p className="text-sm text-red-600 mt-2">{labels.last_one}</p>
        )}
      </form>
    )
  }

  return (
    <p className="mt-6 font-bold text-red-600">{labels.out_of_stock}</p>
  )
}
